import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button, Icon } from "@astrum/ui";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import "./daily-reminder.css";

/**
 * A modal that confronts someone who still owes today's daily report.
 *
 * The rail's red dot is easy to ignore — people who are required to check in
 * kept forgetting. This is deliberately harder to miss: when the viewer owes a
 * report, a dialog appears with one button that takes them straight to the form.
 *
 * Restraint so it nags rather than harasses:
 *  - "Later" snoozes it for the rest of *this* local day (keyed on the server's
 *    `today`), so it does not re-pop on every navigation — but it returns the
 *    next day if they still owe one, which is the whole point.
 *  - It never shows while they are already on the daily-report page (they are
 *    already where the button would send them), and it re-checks on navigation
 *    so it closes itself the moment the report is filed.
 *  - Only people who are actually required to report ever see it; the status
 *    endpoint already folds `checkInRequired`, workday, and "reported today"
 *    into `owesReport`.
 */

type CheckinStatus = { owesReport: boolean; today: string };

const SNOOZE_KEY = "astrum.dailyReport.snoozedFor";

/** The local day the reminder is snoozed for, tolerating a locked-down browser. */
function readSnooze(): string | null {
  try {
    return localStorage.getItem(SNOOZE_KEY);
  } catch {
    return null;
  }
}

function writeSnooze(day: string): void {
  try {
    localStorage.setItem(SNOOZE_KEY, day);
  } catch {
    // Best-effort: a browser that refuses the write just means the reminder may
    // reappear on the next navigation this day. No worse than not snoozing.
  }
}

export function DailyReportReminder() {
  const { viewer } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CheckinStatus | null>(null);

  // Re-read on navigation (and hourly, for a day that rolls over or a report
  // window that opens mid-session) — the same cadence useOwesReport uses, so
  // filing the report closes this without a manual refresh.
  useEffect(() => {
    if (!viewer) return;
    let cancelled = false;
    const check = () =>
      api
        .get<CheckinStatus>("/me/checkin-status")
        .then((res) => {
          if (!cancelled) setStatus(res);
        })
        .catch(() => undefined);
    check();
    const timer = setInterval(check, 60 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [viewer, location.pathname]);

  const fileNow = useCallback(() => {
    setStatus((prev) => (prev ? { ...prev, owesReport: false } : prev));
    navigate("/daily-report");
  }, [navigate]);

  const later = useCallback(() => {
    if (status) writeSnooze(status.today);
    setStatus((prev) => (prev ? { ...prev, owesReport: false } : prev));
  }, [status]);

  // Nothing owed, no status yet, already on the form, or snoozed for today.
  const owes = status?.owesReport === true;
  const onReportPage = location.pathname.startsWith("/daily-report");
  const snoozed = status ? readSnooze() === status.today : true;
  if (!owes || onReportPage || snoozed) return null;

  /*
   * Deliberately its own overlay rather than the shared Modal: this must not be
   * dismissible by clicking the backdrop or pressing Escape — the two ways
   * people reflexively wave a dialog away without reading it, which is exactly
   * how the red dot came to be ignored. The only ways out are the two buttons,
   * both of which resolve the reminder honestly (file it, or snooze for today).
   */
  return (
    <div className="daily-reminder-backdrop" role="presentation">
      <div className="daily-reminder" role="alertdialog" aria-modal="true" aria-labelledby="daily-reminder-title">
        <span className="daily-reminder__icon" aria-hidden="true">
          <Icon name="clock" size={22} />
        </span>
        <h2 className="daily-reminder__title" id="daily-reminder-title">
          Your daily report is due
        </h2>
        <p className="daily-reminder__body">
          You haven&rsquo;t filed today&rsquo;s daily report yet. It takes a minute — your manager sees it as part of
          the team&rsquo;s day.
        </p>
        <div className="daily-reminder__actions">
          <Button variant="ghost" onClick={later}>
            Later today
          </Button>
          <Button onClick={fileNow}>File it now</Button>
        </div>
      </div>
    </div>
  );
}
